import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { startTransition, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { IOSLayout } from './components/ios/IOSLayout';
import { DesktopThreeColumnLayout } from './components/desktop/DesktopThreeColumnLayout';
import { Tasks } from './Tasks';
import { TaskV2 } from './TaskV2';
import { ProjectV2 } from './ProjectV2';
import { Calendar } from './Calendar';
import { Focus } from './Focus';
import { ClockMode } from './ClockMode';
import { ChatListPage } from './ChatListPage';
import { ChatRouter } from './ChatRouter';
import { ChatDetail } from './ChatDetail';
import { Me } from './Me';
import { TaskCreate } from './TaskCreate';
import { ProjectDetail } from './ProjectDetail';
import { SharedProjectDetail } from './SharedProjectDetail';
import { Login } from './Login';
import { DeveloperSettings } from './DeveloperSettings';
import { PersonaOnboarding } from './PersonaOnboarding';
import { AccountManagement } from './AccountManagement';
import { InviteCode } from './InviteCode';
import { ChatPersonalizationSettings } from './ChatPersonalizationSettings';
import { CharacterMarket } from './CharacterMarket';
import { CharacterEditor } from './CharacterEditor';
import { UppyProvider } from './components/chatInput/UppyProvider';
import { AudioRecorderTest } from './AudioRecorderTest';
import { PrivateRoute } from './components/PrivateRoute';
import {
  layoutModeAtom,
  LayoutMode,
} from '../../atoms/v3/uiAtoms';
import { navigationRequestAtom } from '../../atoms/v3/navigationAtoms';
import { lastActiveChatViewAtom, selectedConversationIdAtom } from '../../atoms/v3/chatAtoms';
import { UpdateModal } from '../../components/v3/UpdateModal';
import { ToastContainer } from '../../components/v3/ToastContainer';
import { useUpdateChecker } from '../../hooks/v3/useUpdateChecker';
import { getPlatformInfo } from '../../utils/platformUtils';
import { useShortcutRegistration } from '../../hooks/useShortcutRegistration';

// 导入 v3 专用样式
import '../../styles/v3.css';

export function AppV3() {
  const location = useLocation();
  const navigate = useNavigate();
  const [layoutMode, setLayoutMode] = useAtom(layoutModeAtom);

  const setLastActiveChatView = useSetAtom(lastActiveChatViewAtom);
  const setSelectedConversationId = useSetAtom(selectedConversationIdAtom);
  const [navigationRequest, setNavigationRequest] = useAtom(navigationRequestAtom);

  // 更新检测功能
  const { checkForUpdates, isSupportedPlatform } = useUpdateChecker();

  // 快捷键注册系统
  useShortcutRegistration();

  const parsePath = (path: string) => {
    if (path.startsWith('/v3/chat-detail')) {
      // 提取聊天ID（如果有）
      let chatId = null;
      if (path.length > '/v3/chat-detail/'.length) {
        chatId = path.substring('/v3/chat-detail/'.length);
      }

      // 设置对话ID
      if (!chatId || chatId === 'new') {
        setSelectedConversationId(undefined);
      } else {
        setSelectedConversationId(chatId);
      }

      // 在桌面模式下，设置视图
      if (layoutMode === 'desktop') {
        setLastActiveChatView('detail');
      }
    } else if (path === '/v3/chat-list') {
      // 设置最后活跃的聊天视图为列表
      // 在桌面模式下，设置视图
      if (layoutMode === 'desktop') {
        console.log(`setLastActiveChatView('list');`)
        setLastActiveChatView('list');
      }
    }
  };

  // 处理所有URL路由变化的逻辑
  useEffect(() => {
    parsePath(location.pathname);
    if(layoutMode === 'desktop' && location.pathname.startsWith('/v3/chat')) {
      startTransition(() => {
        navigate('/v3/taskv2', { replace: true });
      });
    }
  }, [location.pathname, layoutMode, navigate, setLastActiveChatView, setSelectedConversationId]);

  // 监听导航请求, 强烈推荐! 会避免desktop强刷
  useEffect(() => {
    if (navigationRequest) {
      console.log('[AppV3] Navigation request detected:', navigationRequest);

      if (layoutMode === 'mobile') {
       //过渡更新
        startTransition(() => {
          navigate(navigationRequest.path, { replace: navigationRequest.replace });
        })
      } else {
        parsePath(navigationRequest.path);
      }
      // 重置导航请求，避免重复导航
      setNavigationRequest(null);
    }
  }, [navigationRequest, navigate, setNavigationRequest]);


  // 监听窗口大小变化，自动切换布局模式
  useEffect(() => {
    const handleResize = () => {
      const newLayoutMode: LayoutMode = window.innerWidth >= 1024 ? 'desktop' : 'mobile';
      if (newLayoutMode !== layoutMode) {
        setLayoutMode(newLayoutMode);
      }
    };

    // 初始化时检查一次
    handleResize();

    // 添加窗口大小变化监听
    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [layoutMode, setLayoutMode]);

  // 在应用启动时检查更新（不依赖用户登录状态）
  // 在 Electron 环境下禁用前端自动更新检查，避免与主进程更新检查冲突
  useEffect(() => {
    const platformInfo = getPlatformInfo();

    // 只在非 Electron 环境下进行自动更新检查
    if (isSupportedPlatform && !platformInfo.isElectron) {
      // 延迟检查更新，确保主界面完全加载
      const timer = setTimeout(() => {
        checkForUpdates();
      }, 2000); // 2秒延迟

      return () => clearTimeout(timer);
    }
  }, [isSupportedPlatform, checkForUpdates]);


  // 根据布局模式选择合适的布局组件
  const LayoutComponent = layoutMode === 'desktop' ? DesktopThreeColumnLayout : IOSLayout;

  return (
    <>
      {/* 更新检测 Modal */}
      <UpdateModal />

      {/* Toast 容器 */}
      <ToastContainer />

      <Routes>
        {/* 登录页面路由 */}
        <Route path="/login" element={<Login />} />

        {/* 邀请码验证页面路由 - 需要登录且使用PrivateRoute保护 */}
        <Route path="/invite-code" element={
          <PrivateRoute>
            <InviteCode />
          </PrivateRoute>
        } />

        {/* 分享项目路由 - 不需要认证 */}
        <Route path="/s/project/:shareId" element={<SharedProjectDetail />} />

      {/* 时钟模式路由 - 独立于主布局，全屏显示 */}
      <Route
        path="/clock-mode"
        element={
          <PrivateRoute>
            <ClockMode />
          </PrivateRoute>
        }
      />

      {/* 主布局路由 - 使用PrivateRoute保护 */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <LayoutComponent />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="chat" replace />} />
        <Route path="taskv2" element={<TaskV2 />} />
        <Route path="projectv2" element={<ProjectV2 />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="focus" element={<Focus />} />
        <Route path="chat" element={<ChatRouter />} />
        <Route path="chat-list" element={<ChatListPage />} />
        <Route path="chat-detail/:id?" element={<ChatDetail />} />
        <Route path="chat-personalization-settings" element={<ChatPersonalizationSettings />} />
        <Route path="audio-test" element={<AudioRecorderTest />} />
        <Route path="me" element={<Me />} />
      </Route>

      {/* 其他详情页面路由 - 使用PrivateRoute保护 */}
      <Route
        path="/task-create"
        element={
          <PrivateRoute>
            <TaskCreate />
          </PrivateRoute>
        }
      />

      <Route
        path="/developer-settings"
        element={
          <PrivateRoute>
            <DeveloperSettings />
          </PrivateRoute>
        }
      />

      <Route
        path="/persona-onboarding"
        element={
          <PrivateRoute>
            <PersonaOnboarding />
          </PrivateRoute>
        }
      />

      <Route
        path="/account-management"
        element={
          <PrivateRoute>
            <AccountManagement />
          </PrivateRoute>
        }
      />

      <Route
        path="/character-market"
        element={
          <PrivateRoute>
            <CharacterMarket />
          </PrivateRoute>
        }
      />

      <Route
        path="/character-editor/:id?"
        element={
          <PrivateRoute>
            <UppyProvider>
              <CharacterEditor />
            </UppyProvider>
          </PrivateRoute>
        }
      />

        {/* 重定向路由 - 确保放在最后 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default AppV3;
